import { FontIcon } from '@fluentui/react';
import { IPLabel, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FilterSwitch } from 'components/FilterSwitch/FilterSwitch';
import TextInfo from 'components/Notification/Info';
import { globalAppName, globalKB_TagTaskCategory } from 'globalConstants';
import Tag, { TagSystemTypes } from 'models/tag';
import { useTranslation } from 'react-i18next';

// Fluent icon per tag system-type ("Category") option.
const CATEGORY_ICON: Partial<Record<TagSystemTypes, string>> = {
  [TagSystemTypes.None]: 'CircleRing',
  [TagSystemTypes.Project]: 'ProjectCollection',
  [TagSystemTypes.MonitoringFinding]: 'View',
  [TagSystemTypes.NonConformity]: 'Warning',
  [TagSystemTypes.Incident]: 'IncidentTriangle',
};

interface ICategoryChipsProps {
  // tagSystemType on the Tag model is a plain number, so keep these numeric to avoid enum-cast churn.
  selected: number | undefined;
  onChange: (systemType: number) => void;
}

//
// The tag "Category" (system-type) selector. Reuses the FilterSwitch pill control from the task
// views so the selected state looks and behaves identically across the app. Clicking the selected
// category again clears it back to "no category". Shared by tag create and edit.
//
const CategoryChips = (props: ICategoryChipsProps) => {
  const { t } = useTranslation(['tag']);

  const options = Tag.getSystemTypeOptions(t)
    .filter((opt) => opt.key !== TagSystemTypes.None)
    .map((opt) => ({
      key: String(opt.key),
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FontIcon iconName={CATEGORY_ICON[opt.key as TagSystemTypes] ?? 'Tag'} aria-hidden={true} />
          {opt.text}
        </span>
      ),
    }));

  return (
    <IPStack>
      <TextInfo
        message={t('tag:Editor.SystemType.Info', { appName: globalAppName })}
        learnMoreLink={globalKB_TagTaskCategory}
      >
        <IPLabel>{t('tag:Editor.SystemType.Label')}</IPLabel>
      </TextInfo>
      <FilterSwitch
        options={options}
        selectedKey={String(props.selected ?? TagSystemTypes.None)}
        onChange={(key) => {
          const systemType = Number(key);
          props.onChange(systemType === props.selected ? TagSystemTypes.None : systemType);
        }}
      />
      <IPStack styles={{ root: { paddingTop: 15 } }}>
        <IPText variant="small">{Tag.GetSystemTypeTextInfo(props.selected, t)}</IPText>
      </IPStack>
    </IPStack>
  );
};

export default CategoryChips;
